import { epgChannelsModel } from '$/epg/channels'
import { PATH, ROUTE_NAME, SEGMENT } from '@/constants'
import { getDataFromUrl } from '@/helpers'
import { type LiveChannel } from '@setplex/tria-api'
import {
  combine,
  createEvent,
  createStore,
  restore,
  sample,
  type Store,
} from 'effector'
import { model as router } from '~/core/router'
import { parseEpgPath, updateEpgPath } from '~/entities/epg/lib'
import { model as session } from '~/entities/session'
import { $route } from '~/processes/navigation'
import { lookbehind } from '~/shared/effector'
import { T, formatTitleForUrl } from '~/shared/helpers'

export const autoSetActiveId = createEvent<{
  channels: LiveChannel[]
  [SEGMENT.CHANNEL]?: string
}>()

export const setActiveId = createEvent<number | null>()

// Reset active channel ID to null, which will trigger selection of first channel from "All" category
// can be called from any page
export const resetChannel = createEvent()

// Filter to check if we're on the channel player page
const $isEpgChannelPage = $route.map((route) => {
  const routeName = route?.routeName ?? ''
  return routeName === ROUTE_NAME.LIVE_CATEGORIES
})

export const $activeId = createStore<number | null>(null) //
  .reset(resetChannel)

sample({
  clock: setActiveId,
  filter: $isEpgChannelPage,
  target: $activeId,
})

sample({
  clock: autoSetActiveId,
  source: $activeId,
  filter: $isEpgChannelPage,
  fn: (current, { [SEGMENT.CHANNEL]: channel }) => {
    const { id } = getDataFromUrl(channel)
    if (id == null) return current // do not change current active id
    const idn = Number(id)
    if (isNaN(idn)) return current // do not change current active id
    return idn
  },
  target: $activeId,
})

//
// persist active channel id to url
//

const pushActiveIdToUrl = createEvent<number | null>()

lookbehind({
  source: $activeId,
  clear: autoSetActiveId,
  within: 0,
  target: pushActiveIdToUrl,
})

// EPG page navigations:
// - make seo friendly url: /region-1/category-2/3 -> /region-1/category-2/channel-3
// - correct title: /region-1/category-2/ch-3 -> /region-1/category-2/channel-3
// (also can be another combinations)
sample({
  clock: [epgChannelsModel.$all, $activeId],
  source: {
    pathname: router.$pathname,
    activeId: $activeId,
    channelsMap: epgChannelsModel.$all,
  },
  filter: ({ pathname, activeId, channelsMap }) => {
    const [channels] = channelsMap
    const channel = channels?.get(Number(activeId))
    // if no title no need to update url
    if (!activeId || activeId !== channel?.id || !channel?.title) return false

    // Check url channel
    const { channelId, channelTitle } = parseEpgPath(pathname ?? '')
    if (!channelId || channelId !== activeId) return false

    const existedChannelSegment = `${channelTitle}-${channelId}`
    const correctChannelSegment = `${formatTitleForUrl(channel?.title)}${channel?.id}`

    return (
      existedChannelSegment !== correctChannelSegment &&
      Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`))
    )
  },
  fn: ({ pathname, activeId, channelsMap }) => {
    if (!activeId) return pathname

    const [channels] = channelsMap
    const channel = channels?.get(Number(activeId))

    return updateEpgPath(pathname, {
      channelId: channel?.id ?? activeId,
      channelTitle: channel?.title ?? '',
    })
  },
  target: router.navigatePush,
})

// update pathname with active channel id
sample({
  clock: pushActiveIdToUrl,
  source: {
    pathname: router.$pathname,
    channelsMap: epgChannelsModel.$all,
  },
  filter: ({ pathname }, id) =>
    Boolean(id) &&
    Boolean(pathname?.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}`)),
  fn: ({ pathname, channelsMap }, id) => {
    if (!id) return pathname
    const channel = channelsMap[0]?.get(id)

    return updateEpgPath(pathname, {
      channelId: id,
      channelTitle: channel?.title ?? '',
    })
  },
  target: router.navigatePush,
})

//
//
//

// currently active channel
export const $active: Store<LiveChannel | null> = combine(
  $activeId,
  epgChannelsModel.$all,
  (id, [channels]) => (id == null ? null : (channels.get(id) ?? null))
)

// select first channel from active list, if there is no channel id in url
sample({
  clock: autoSetActiveId,
  source: {
    id: $activeId,
    active: $active,
  },
  filter: ({ id, active }, { channels }) =>
    id == null && active == null && channels.length > 0,
  fn: (_, { channels }) => {
    return channels[0].id
  },
  target: setActiveId,
})

// load channel, if there is no active channel in loaded, but there is active channel id
sample({
  clock: autoSetActiveId,
  source: {
    id: $activeId,
    active: $active,
    pending: epgChannelsModel.loadOneFx.pending,
  },
  filter: ({ id, active, pending }) => id != null && active == null && !pending,
  fn: ({ id }) => {
    if (id == null) {
      throw new Error('Active channel id is required to load a channel')
    }
    return id
  },
  target: epgChannelsModel.loadOneFx,
})

// if there is still no active channel - reset channel id or select first channel from list
sample({
  clock: epgChannelsModel.loadOneFx.finally,
  source: {
    active: $active,
    autoset: restore(autoSetActiveId, null),
  },
  filter: ({ active }) => active == null,
  fn: ({ autoset }) =>
    autoset != null && autoset.channels.length > 0
      ? autoset.channels[0].id
      : null,
  target: setActiveId,
})

// store that indicates if active channel is ready to be displayed
// becomes true when channels are loaded and active channel is set
export const $activeChannelIsReady = createStore<boolean>(false).reset(
  session.$isAuthenticated
)

// set active channel ready when:
// - active channel is set manually
// - active channel is set automatically
// - all channels are loaded
sample({
  clock: [setActiveId, autoSetActiveId, epgChannelsModel.$allIsReady],
  source: {
    allIsReady: epgChannelsModel.$allIsReady,
    isEpgPage: $isEpgChannelPage,
  },
  // only set ready if channels are actually loaded and this flag is only set on epg page (to avoid setting when open simple live player)
  filter: ({ allIsReady, isEpgPage }) => {
    return Boolean(allIsReady && isEpgPage)
  },
  fn: T,
  target: $activeChannelIsReady,
})
